import * as React from "react";

export interface DebouncedCallback<T extends (...args: never[]) => unknown> {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  /** Cancel the pending invocation, if any. Safe to call when nothing is pending. */
  cancel: () => void;
}

/*
 * Only execute callback delay_ms time after the most recent attempt.
 * i.e. callback is executed "after the dust has settled".
 *
 * The pending timer is cancelled on unmount so the wrapped callback never
 * fires against an unmounted component (avoids post-unmount side effects).
 *
 * The returned function exposes a `.cancel()` method that drops the pending
 * invocation. Useful when a synchronous event needs to supersede the debounced
 * one (e.g., a click that should fire immediately, overriding queued keystrokes).
 */
export function useDebouncedCallback<
  T extends (...args: Parameters<T>) => ReturnType<T>,
>(fn: T, delay_ms: number = 0): DebouncedCallback<T> {
  const timeout = React.useRef<number>();

  const cancel = React.useCallback(() => {
    window.clearTimeout(timeout.current);
    timeout.current = undefined;
  }, []);

  React.useEffect(() => {
    return cancel;
  }, [cancel]);

  return React.useMemo(() => {
    const debounced = (...args: Parameters<T>): ReturnType<T> | undefined => {
      let result: ReturnType<T> | undefined = undefined;

      const later = () => {
        timeout.current = undefined;
        result = fn(...args);
      };

      window.clearTimeout(timeout.current);
      timeout.current = window.setTimeout(later, delay_ms);

      return result;
    };
    debounced.cancel = cancel;
    return debounced;
  }, [fn, delay_ms, cancel]);
}
